import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllSeasonsAction,
  getAllZonesAction,
  getAllRegionsAction,
  getAllAreasAction,
  getAllDistrictsAction,
  getAllMandalsAction,
  createPostSeasonActivityAction,
  clearCreatePostSeasonActivityAction,
  updatePostSeasonActivityAction,
  clearUpdatePostSeasonActivityAction,
  getPostSeasonActivityByIdAction,
  clearGetPostSeasonActivityByIdAction,
} from '../../../store/actions';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';

const CreatePostActivityAMRM = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, activity: existingActivity } = location.state || {};
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  const { successData: seasons } = useSelector((state) => state.getAllSeasonsReducer);
  const seasonList = seasons?.data?.seasons || [];

  const { successData: zones } = useSelector((state) => state.getAllZonesReducer);
  const zoneList = zones?.data?.zones || [];

  const { successData: regions } = useSelector((state) => state.getAllRegionsReducer);
  const regionList = regions?.data?.regions || [];

  const { successData: areas } = useSelector((state) => state.getAllAreasReducer);
  const areaList = areas?.data?.areas || [];

  const { successData: districts } = useSelector((state) => state.getAllDistrictsReducer);
  const districtList = districts?.data?.districts || [];

  const { successData: postActivityResponse, error, errorInfo } = useSelector(
    (state) => state.createPostSeasonActivityReducer
  );
  const {
    successData: updateResponse,
    error: updateError,
    errorInfo: updateErrorInfo,
  } = useSelector((state) => state.updatePostSeasonActivityReducer);
  const { successData: postSeasonActivityData } = useSelector(
    (state) => state.getPostSeasonActivityByIdReducer
  );

  const { successData: mandals } = useSelector((state) => state.getAllMandalsReducer);
  const mandalList = mandals?.data?.mandals || [];

  useEffect(() => {
    dispatch(getAllSeasonsAction());
    dispatch(getAllZonesAction());
    dispatch(getAllRegionsAction());
    dispatch(getAllAreasAction());
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());

    return () => {
      dispatch(clearGetPostSeasonActivityByIdAction());
    };
  }, [dispatch]);

  useEffect(() => {
    if ((isViewMode || isEditMode) && existingActivity?.id) {
      dispatch(
        getPostSeasonActivityByIdAction({
          endPoint: `target/post-season/${existingActivity.id}`,
        })
      );
    }

    return () => {
      dispatch(clearGetPostSeasonActivityByIdAction());
    };
  }, [dispatch, existingActivity, isViewMode, isEditMode]);

  const getInitialValues = () => {
    if (isEditMode || isViewMode) {
      const data = postSeasonActivityData?.data || {};
      let zone = '';
      let region = '';
      let area = '';
      let district = '';
      let mandal = '';

      if (data.target_area_type === 'mandal') {
        const mandalObj = mandalList.find((m) => String(m.mandalId) === String(data.target_area_id));
        mandal = mandalObj?.mandalId ?? data.target_area_id ?? '';
        district = mandalObj?.districtId ?? '';
        const districtObj = districtList.find((d) => String(d.districtId) === String(district));
        area = districtObj?.areaId ?? '';
        const areaObj = areaList.find((a) => String(a.areaId) === String(area));
        region = areaObj?.regionId ?? '';
        const regionObj = regionList.find((r) => String(r.regionId) === String(region));
        zone = regionObj?.zoneId ?? '';
      } else if (data.target_area_type === 'district') {
        const districtObj = districtList.find((d) => String(d.districtId) === String(data.target_area_id));
        district = districtObj?.districtId ?? data.target_area_id ?? '';
        area = districtObj?.areaId ?? '';
        const areaObj = areaList.find((a) => String(a.areaId) === String(area));
        region = areaObj?.regionId ?? '';
        const regionObj = regionList.find((r) => String(r.regionId) === String(region));
        zone = regionObj?.zoneId ?? '';
      }

      return {
        seasonId: data.season_id ?? '',
        targetLocationType: data.target_area_type ?? '',
        zone: zone || '',
        region: region || '',
        area: area || '',
        district: district || '',
        mandal: mandal || '',
        dvCount: data.dv_count ?? '',
        fvCount: data.fv_count ?? '',
        fdCount: data.fd_count ?? '',
      };
    }

    // Autofill for RM/AM
    let autofill = {
      seasonId: '',
      targetLocationType: '',
      zone: '',
      region: '',
      area: '',
      district: '',
      mandal: '',
      dvCount: '',
      fvCount: '',
      fdCount: '',
    };
    try {
      const userDetails = JSON.parse(localStorage.getItem('userDetails'));
      const role = (userDetails?.role || '').toUpperCase();
      const userId = userDetails?.user_id || userDetails?.id;
      if (role === 'RM') {
        // Find region where inchargeUserId matches userId
        const rmRegion = regionList.find(r => String(r.inchargeUserId) === String(userId));
        const rmZone = rmRegion ? zoneList.find(z => String(z.zoneId) === String(rmRegion.zoneId)) : null;
        autofill.region = rmRegion ? rmRegion.regionId : '';
        autofill.zone = rmZone ? rmZone.zoneId : '';
      }
      if (role === 'AM') {
        // Find area where inchargeUserId matches userId
        const amArea = areaList.find(a => String(a.inchargeUserId) === String(userId));
        const amRegion = amArea ? regionList.find(r => String(r.regionId) === String(amArea.regionId)) : null;
        const amZone = amArea ? zoneList.find(z => String(z.zoneId) === String(amArea.zoneId)) : null;
        autofill.area = amArea ? amArea.areaId : '';
        autofill.region = amRegion ? amRegion.regionId : '';
        autofill.zone = amZone ? amZone.zoneId : '';
      }
    } catch (e) {}
    return autofill;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(),
    onSubmit: (values) => {
      const targetId =
        values.targetLocationType === 'mandal'
          ? values.mandal !== ''
            ? Number(values.mandal)
            : null
          : values.targetLocationType === 'district'
          ? values.district !== ''
            ? Number(values.district)
            : null
          : null;

      const payload = {
        season_id: Number(values.seasonId),
        target_area_type: values.targetLocationType,
        target_area_id: targetId,
        dv_count: Number(values.dvCount || 0),
        fv_count: Number(values.fvCount || 0),
        fd_count: Number(values.fdCount || 0),
        is_active: existingActivity?.is_active ?? true,
      };

      if (isEditMode && existingActivity) {
        const id =
          existingActivity.id ||
          existingActivity.postSeasonActivityId ||
          existingActivity.post_activity_id;

        dispatch(
          updatePostSeasonActivityAction({
            endPoint: `target/post-season/${id}`,
            ...payload,
          })
        );
      } else {
        dispatch(createPostSeasonActivityAction(payload));
      }
    },
  });

  const fieldsByType = {
    mandal: ['zone', 'region', 'area', 'district', 'mandal'],
    district: ['zone', 'region', 'area', 'district'],
  };
  const visibleFields = fieldsByType[formik.values.targetLocationType] || [];

  useEffect(() => {
    ['zone', 'region', 'area', 'district'].forEach((field) => {
      if (!visibleFields.includes(field)) {
        formik.setFieldValue(field, '');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.targetLocationType]);

  const filteredRegions = regionList.filter((r) => String(r.zoneId) === String(formik.values.zone));
  const filteredAreas = areaList.filter((a) => String(a.regionId) === String(formik.values.region));
  const filteredDistricts = districtList.filter((d) => String(d.areaId) === String(formik.values.area));
  const filteredMandals = mandalList.filter((m) => String(m.districtId) === String(formik.values.district));

  useEffect(() => {
    if (!isEditMode && postActivityResponse) {
      toast.success('POST activity created successfully!', { id: 'post-activity-success' });
      formik.resetForm();
      navigate('/amrm/postActivity');
    }

    if (isEditMode && updateResponse) {
      toast.success('POST activity updated successfully!', { id: 'post-activity-success' });
      navigate('/amrm/postActivity');
    }

    const errMsg =
      updateErrorInfo?.message ||
      updateErrorInfo ||
      errorInfo?.message ||
      errorInfo;

    if (updateError || updateErrorInfo || error || errorInfo) {
      toast.error(errMsg || 'Failed to process POST activity. Please try again.', {
        id: 'post-activity-error',
      });
    }

    return () => {
      dispatch(clearCreatePostSeasonActivityAction());
      dispatch(clearUpdatePostSeasonActivityAction());
    };
  }, [
    postActivityResponse,
    error,
    errorInfo,
    updateResponse,
    updateError,
    updateErrorInfo,
    isEditMode,
    dispatch,
    navigate,
  ]);

  return (
    <Box sx={{ minHeight: '100vh', mt: { xs:8,sm: 8, md: 0 } }}>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <form onSubmit={formik.handleSubmit}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              flexDirection={{ xs: 'column', md: 'row' }}
              mb={3}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {isViewMode
                    ? 'View POST Information'
                    : isEditMode
                    ? 'Edit POST Information'
                    : 'POST Information'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isViewMode
                    ? 'View post activity'
                    : isEditMode
                    ? 'Edit post activity'
                    : 'Create and manage post activity'}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={seasonList}
                  value={seasonList.find((s) => String(s.seasonId) === String(formik.values.seasonId)) || null}
                  onChange={(e, val) => formik.setFieldValue('seasonId', val ? val.seasonId : '')}
                  getOptionLabel={(option) =>
                    option?.name && option?.year ? `${option.name} (${option.year})` : option?.name || ''
                  }
                  isOptionEqualToValue={(option, value) => String(option.seasonId) === String(value.seasonId)}
                  disabled={isViewMode}
                  clearOnEscape
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Season"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                    />
                  )}
                />
              </Grid>

              <Grid item size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={['district', 'mandal']}
                  value={formik.values.targetLocationType || null}
                  onChange={(e, val) => formik.setFieldValue('targetLocationType', val || '')}
                  getOptionLabel={(option) => {
                    if (option === 'district') return 'District';
                    if (option === 'mandal') return 'Mandal';
                    return option || '';
                  }}
                  disabled={isViewMode}
                  clearOnEscape
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Target Location Type"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} mt={2}>
              {visibleFields.includes('zone') && (
                <Grid item size={{ xs: 12, sm: 3, md: 3 }}>
                  <TextField
                    size="small"
                    select
                    label="Zone"
                    name="zone"
                    value={formik.values.zone}
                    disabled={isViewMode}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : Number(e.target.value);
                      formik.setFieldValue('zone', val);
                      formik.setFieldValue('region', '');
                      formik.setFieldValue('area', '');
                      formik.setFieldValue('district', '');
                    }}
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                  >
                    <MenuItem value="">Select Zone</MenuItem>
                    {zoneList.map((z) => (
                      <MenuItem key={z.zoneId} value={z.zoneId}>
                        {z.zoneName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {visibleFields.includes('region') && (
                <Grid item size={{ xs: 12, sm: 3, md: 3 }}>
                  <Autocomplete
                    options={filteredRegions}
                    value={
                      filteredRegions.find((r) => String(r.regionId) === String(formik.values.region)) ||
                      null
                    }
                    onChange={(e, val) => {
                      formik.setFieldValue('region', val ? val.regionId : '');
                      formik.setFieldValue('area', '');
                      formik.setFieldValue('district', '');
                    }}
                    getOptionLabel={(option) => option?.regionName || ''}
                    isOptionEqualToValue={(option, value) => String(option.regionId) === String(value.regionId)}
                    disabled={isViewMode}
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        label="Region"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                      />
                    )}
                  />
                </Grid>
              )}

              {visibleFields.includes('area') && (
                <Grid item size={{ xs: 12, sm: 3, md: 3 }}>
                  <Autocomplete
                    options={filteredAreas}
                    value={filteredAreas.find((a) => String(a.areaId) === String(formik.values.area)) || null}
                    onChange={(e, val) => {
                      formik.setFieldValue('area', val ? val.areaId : '');
                      formik.setFieldValue('district', '');
                      formik.setFieldValue('mandal', '');
                    }}
                    getOptionLabel={(option) => option?.areaName || ''}
                    isOptionEqualToValue={(option, value) => String(option.areaId) === String(value.areaId)}
                    disabled={isViewMode}
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        label="Area"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                      />
                    )}
                  />
                </Grid>
              )}

              {visibleFields.includes('district') && (
                <Grid item size={{ xs: 12, sm: 3, md: 3 }}>
                  <Autocomplete
                    options={filteredDistricts}
                    value={
                      filteredDistricts.find((d) => String(d.districtId) === String(formik.values.district)) ||
                      null
                    }
                    onChange={(e, val) => {
                      formik.setFieldValue('district', val ? val.districtId : '');
                      formik.setFieldValue('mandal', '');
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => String(option.districtId) === String(value.districtId)}
                    disabled={isViewMode}
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        label="District"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                      />
                    )}
                  />
                </Grid>
              )}

              {visibleFields.includes('mandal') && (
                <Grid item size={{ xs: 12, sm: 3, md: 3 }}>
                  <Autocomplete
                    options={filteredMandals}
                    value={
                      filteredMandals.find((m) => String(m.mandalId) === String(formik.values.mandal)) ||
                      null
                    }
                    onChange={(e, val) => {
                      formik.setFieldValue('mandal', val ? val.mandalId : '');
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => String(option.mandalId) === String(value.mandalId)}
                    disabled={isViewMode}
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        label="Mandal"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="DV Count"
                  name="dvCount"
                  placeholder="Enter DV count"
                  value={formik.values.dvCount}
                  onChange={formik.handleChange}
                  disabled={isViewMode}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="FV Count"
                  name="fvCount"
                  placeholder="Enter FV count"
                  value={formik.values.fvCount}
                  onChange={formik.handleChange}
                  disabled={isViewMode}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                />
              </Grid>

              <Grid item size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="FD Count"
                  name="fdCount"
                  placeholder="Enter FD count"
                  value={formik.values.fdCount}
                  onChange={formik.handleChange}
                  disabled={isViewMode}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                />
              </Grid>
            </Grid>

            <Box
              display="flex"
              justifyContent="flex-end"
              gap={2}
              mt={4}
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  if (!isViewMode) formik.resetForm();
                  navigate('/amrm/postActivity');
                }}
              >
                {isViewMode ? 'Back' : 'Cancel'}
              </Button>

              <Button variant="contained" type="submit" disabled={isViewMode} sx={{ backgroundColor: '#4F46E5' }}>
                {isEditMode ? 'Update' : 'Submit'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreatePostActivityAMRM;
